/* Shopify-compatible predictive-search.js
   Converted to a plain script (IIFE). Paste entire file into Assets/predictive-search.js
*/
(function () {
  // Try to read theme globals
  const ThemeGlobal = window.Theme || {};
  // Basic fallbacks
  let Component = ThemeGlobal.Component || window.Component;
  if (!Component) {
    Component = class extends HTMLElement {
      constructor() {
        super();
        this.refs = {};
      }
      connectedCallback() {}
      disconnectedCallback() {}
    };
  }

  const utilities = (ThemeGlobal && ThemeGlobal.utilities) || window.ThemeUtilities || window;
  const debounce = (utilities && utilities.debounce) || (fn => fn);
  const onAnimationEnd = (utilities && utilities.onAnimationEnd) || function (el, cb) {
    if (!el || typeof cb !== 'function') return;
    el.addEventListener('animationend', cb, { once: true });
    setTimeout(cb, 500);
  };
  const prefersReducedMotion = (utilities && utilities.prefersReducedMotion) || function () { return false; };
  const onDocumentLoaded = (utilities && utilities.onDocumentLoaded) || function (cb) {
    if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', cb);
    else cb();
  };

  const sectionRenderer = ThemeGlobal.sectionRenderer || window.sectionRenderer || { getSectionHTML: () => Promise.resolve(null) };
  const morph = ThemeGlobal.morph || window.morph || function (el, html) { if (el) el.innerHTML = html || ''; };
  const RecentlyViewed = ThemeGlobal.RecentlyViewed || window.RecentlyViewed || { getProducts: () => [], clearProducts: () => {} };
  const DialogCloseEvent = ThemeGlobal.DialogCloseEvent || window.DialogCloseEvent || { eventName: 'dialog-close' };

  class PredictiveSearchComponent extends Component {
    constructor() {
      super();
      if (!this.refs) this.refs = {};
      this.requiredRefs = ['searchInput', 'predictiveSearchResults', 'resetButton'];
      this._controller = new AbortController();
      this._activeFetch = null;
      this._isKeyboardNavigation = false;
    }

    get dialog() {
      return this.closest ? this.closest('dialog-component') : null;
    }

    connectedCallback() {
      if (super.connectedCallback) super.connectedCallback();
      const dialog = this.dialog;
      const { signal } = this._controller;

      try {
        if (this.refs && this.refs.searchInput && this.refs.searchInput.value && this.refs.searchInput.value.length > 0) {
          this._showResetButton();
        }
      } catch (e) {}

      if (dialog) {
        document.addEventListener('keydown', this._handleKeyboardShortcut, { signal });
        dialog.addEventListener(DialogCloseEvent.eventName, this._handleDialogClose, { signal });
        this.addEventListener('click', this._handleModalClick, { signal });
      }

      onDocumentLoaded(() => {
        try { this.resetSearch(false); } catch (e) {}
      });
    }

    _handleModalClick = (event) => {
      const target = event.target;
      const isInteractiveElement =
        target instanceof HTMLButtonElement ||
        target instanceof HTMLAnchorElement ||
        target instanceof HTMLInputElement ||
        (target.closest && (target.closest('button') || target.closest('a') || target.closest('input')));

      if (!isInteractiveElement && this.refs && this.refs.searchInput) {
        try { this.refs.searchInput.focus(); } catch (e) {}
      }
    };

    disconnectedCallback() {
      if (super.disconnectedCallback) super.disconnectedCallback();
      try { this._controller.abort(); } catch (e) {}
    }

    _handleKeyboardShortcut = (event) => {
      if (event.metaKey && event.key === 'k') {
        if (this.dialog && typeof this.dialog.toggleDialog === 'function') this.dialog.toggleDialog();
      }
    };

    _handleDialogClose = () => {
      this._resetSearch();
    };

    get _allResultsItems() {
      try {
        const containers = Array.from(
          this.querySelectorAll(
            '.predictive-search-results__wrapper-queries, ' +
              '.predictive-search-results__wrapper-products, ' +
              '.predictive-search-results__list'
          )
        );

        const allItems = containers
          .flatMap((container) => {
            if (container.classList.contains('predictive-search-results__wrapper-products')) {
              return Array.from(container.querySelectorAll('.predictive-search-results__card'));
            }
            return Array.from(container.querySelectorAll('[ref="resultsItems[]"], .predictive-search-results__card'));
          })
          .filter((item) => item instanceof HTMLElement);

        return allItems;
      } catch (e) {
        return [];
      }
    }

    get _currentIndex() {
      try {
        return this._allResultsItems?.findIndex((item) => item.getAttribute('aria-selected') === 'true') ?? -1;
      } catch (e) {
        return -1;
      }
    }

    set _currentIndex(index) {
      try {
        if (!this._allResultsItems?.length) return;
        this._allResultsItems.forEach((item) => item.classList.remove('keyboard-focus'));
        for (const [itemIndex, item] of this._allResultsItems.entries()) {
          if (itemIndex === index) {
            item.setAttribute('aria-selected', 'true');
            if (this._isKeyboardNavigation) item.classList.add('keyboard-focus');
            item.scrollIntoView({ behavior: prefersReducedMotion() ? 'instant' : 'smooth', block: 'nearest' });
          } else {
            item.removeAttribute('aria-selected');
          }
        }
        if (this.refs && this.refs.searchInput) this.refs.searchInput.focus();
      } catch (e) {}
    }

    get _currentItem() {
      try { return this._allResultsItems?.[this._currentIndex]; } catch (e) { return null; }
    }

    onSearchKeyDown = (event) => {
      if (event.key === 'Escape') {
        this._resetSearch();
        return;
      }

      if (!this._allResultsItems?.length || event.key === 'ArrowLeft' || event.key === 'ArrowRight') return;

      const currentIndex = this._currentIndex;
      const totalItems = this._allResultsItems.length;

      switch (event.key) {
        case 'ArrowDown':
          this._isKeyboardNavigation = true;
          event.preventDefault();
          this._currentIndex = currentIndex < totalItems - 1 ? currentIndex + 1 : 0;
          break;
        case 'Tab':
          this._isKeyboardNavigation = true;
          event.preventDefault();
          this._currentIndex = event.shiftKey ? (currentIndex > 0 ? currentIndex - 1 : totalItems - 1)
                                               : (currentIndex < totalItems - 1 ? currentIndex + 1 : 0);
          break;
        case 'ArrowUp':
          this._isKeyboardNavigation = true;
          event.preventDefault();
          this._currentIndex = currentIndex > 0 ? currentIndex - 1 : totalItems - 1;
          break;
        case 'Enter': {
          const singleResultContainer = this.refs.predictiveSearchResults && this.refs.predictiveSearchResults.querySelector('[data-single-result-url]');
          if (singleResultContainer instanceof HTMLElement && singleResultContainer.dataset.singleResultUrl) {
            event.preventDefault();
            window.location.href = singleResultContainer.dataset.singleResultUrl;
            return;
          }
          if (this._currentIndex >= 0) {
            event.preventDefault();
            this._currentItem?.querySelector('a')?.click();
          } else {
            const searchUrl = new URL((ThemeGlobal.routes && ThemeGlobal.routes.search_url) || '/search', location.origin);
            searchUrl.searchParams.set('q', (this.refs.searchInput && this.refs.searchInput.value) || '');
            window.location.href = searchUrl.toString();
          }
          break;
        }
      }
    };

    clearRecentlyViewedProducts(event) {
      event.stopPropagation();
      RecentlyViewed.clearProducts();
      const { recentlyViewedItems, recentlyViewedTitle, recentlyViewedWrapper } = this.refs;
      const allRecentlyViewedElements = [...(recentlyViewedItems || []), ...(recentlyViewedTitle || [])];
      if (allRecentlyViewedElements.length === 0) return;
      if (recentlyViewedWrapper) {
        recentlyViewedWrapper.classList.add('removing');
        onAnimationEnd(recentlyViewedWrapper, () => recentlyViewedWrapper.remove());
      }
    }

    resetSearch = debounce((keepFocus = true) => {
      if (keepFocus && this.refs && this.refs.searchInput) {
        try { this.refs.searchInput.focus(); } catch (e) {}
      }
      this._resetSearch();
    }, 100);

    search = debounce((event) => {
      if (!event || !event.inputType) return;
      const searchTerm = (this.refs.searchInput && this.refs.searchInput.value.trim()) || '';
      this._currentIndex = -1;
      if (!searchTerm.length) {
        this._resetSearch();
        return;
      }
      this._showResetButton();
      this._getSearchResults(searchTerm);
    }, 200);

    _resetScrollPositions() {
      requestAnimationFrame(() => {
        try {
          const resultsInner = this.refs.predictiveSearchResults && this.refs.predictiveSearchResults.querySelector('.predictive-search-results__inner');
          if (resultsInner instanceof HTMLElement) resultsInner.scrollTop = 0;
          const formContent = this.querySelector('.predictive-search-form__content');
          if (formContent instanceof HTMLElement) formContent.scrollTop = 0;
        } catch (e) {}
      });
    }

    async _getSearchResults(searchTerm) {
      if (!this.dataset.sectionId) return;
      const url = new URL((ThemeGlobal.routes && ThemeGlobal.routes.predictive_search_url) || '/search/suggest.json', location.origin);
      url.searchParams.set('q', searchTerm);
      url.searchParams.set('resources[limit_scope]', 'each');
      const { predictiveSearchResults } = this.refs;
      const abortController = this._createAbortController();
      sectionRenderer
        .getSectionHTML(this.dataset.sectionId, false, url)
        .then((resultsMarkup) => {
          if (!resultsMarkup) return;
          if (abortController.signal.aborted) return;
          morph(predictiveSearchResults, resultsMarkup);
          this._resetScrollPositions();
        })
        .catch((error) => {
          if (abortController.signal.aborted) return;
          throw error;
        });
    }

    async _getRecentlyViewedProductsMarkup() {
      if (!this.dataset.sectionId) return null;
      const viewedProducts = RecentlyViewed.getProducts();
      if (viewedProducts.length === 0) return null;
      const url = new URL((ThemeGlobal.routes && ThemeGlobal.routes.search_url) || '/search', location.origin);
      url.searchParams.set('q', viewedProducts.map((id) => `id:${id}`).join(' OR '));
      url.searchParams.set('resources[type]', 'product');
      return sectionRenderer.getSectionHTML(this.dataset.sectionId, false, url);
    }

    _hideResetButton() {
      try {
        const resetButton = (this.refs && this.refs.resetButton) || this.querySelector('.predictive-search__reset, .search-reset-button, [data-reset-search]');
        if (resetButton) resetButton.hidden = true;
      } catch (e) {}
    }

    _showResetButton() {
      try {
        const resetButton = (this.refs && this.refs.resetButton) || this.querySelector('.predictive-search__reset, .search-reset-button, [data-reset-search]');
        if (resetButton) resetButton.hidden = false;
      } catch (e) {}
    }

    _createAbortController() {
      const abortController = new AbortController();
      if (this._activeFetch) {
        try { this._activeFetch.abort(); } catch (e) {}
      }
      this._activeFetch = abortController;
      return abortController;
    }

    _resetSearch = async () => {
      const { predictiveSearchResults, searchInput } = this.refs;
      const emptySectionId = 'predictive-search-empty';
      this._currentIndex = -1;
      if (searchInput) searchInput.value = '';
      this._hideResetButton();
      const abortController = this._createAbortController();
      const url = new URL(window.location.href);
      url.searchParams.delete('page');
      const emptySectionMarkup = await sectionRenderer.getSectionHTML(emptySectionId, false, url);
      const parsedEmptySectionMarkup = new DOMParser().parseFromString(emptySectionMarkup, 'text/html').querySelector('.predictive-search-empty-section');
      if (!parsedEmptySectionMarkup) throw new Error('No empty section markup found');
      const viewedProducts = RecentlyViewed.getProducts();
      if (viewedProducts.length > 0) {
        const recentlyViewedMarkup = await this._getRecentlyViewedProductsMarkup();
        if (!recentlyViewedMarkup) return;
        const parsedRecentlyViewedMarkup = new DOMParser().parseFromString(recentlyViewedMarkup, 'text/html');
        const recentlyViewedProductsHtml = parsedRecentlyViewedMarkup.getElementById('predictive-search-products');
        if (!recentlyViewedProductsHtml) return;
        for (const child of recentlyViewedProductsHtml.children) {
          if (child instanceof HTMLElement) child.setAttribute('ref', 'recentlyViewedWrapper');
        }
        const collectionElement = parsedEmptySectionMarkup.querySelector('#predictive-search-products');
        if (!collectionElement) return;
        collectionElement.prepend(...recentlyViewedProductsHtml.children);
      }
      if (abortController.signal.aborted) return;
      morph(predictiveSearchResults, parsedEmptySectionMarkup);
      this._resetScrollPositions();
    };
  }

  if (!customElements.get('predictive-search-component')) {
    customElements.define('predictive-search-component', PredictiveSearchComponent);
  }
})();
